import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaAward, FaLandmark, FaRoad, FaIndustry, FaCheck, FaCertificate } from 'react-icons/fa';
import { ISO } from '../lib/contact';

// Grouped for scanning; organisation names are unchanged.
const groups = [
    {
        title: "Government & Municipal Bodies",
        icon: FaLandmark,
        organizations: [
            "Public Works Department (Maharashtra PWD)",
            "City and Industrial Development Corporation (CIDCO)",
            "Thane Municipal Corporation, Thane, Maharashtra",
            "Navi Mumbai Municipal Corporation (NMMC)",
            "Maharashtra Industrial Development Corporation (MIDC)",
            "Ratnagiri Municipal Council",
        ],
    },
    {
        title: "Infrastructure & Tollway Developers",
        icon: FaRoad,
        organizations: [
            "Indian Road Builder (IRB)",
            "SMC Infrastructure Pvt. Ltd.",
            "Ashwini Infradevelopments Pvt. Ltd.",
            "J. Kumar Infraprojects Ltd.",
            "A. N. Enterprises Infrastructure Service Pvt. Ltd.",
            "MEP Infrastructure Pvt. Ltd.",
            "Sion Panvel Tollways Pvt. Ltd.",
            "Pune Solapur Expressways Pvt. Ltd.",
        ],
    },
    {
        title: "Corporate & Private Sector",
        icon: FaIndustry,
        organizations: [
            "Larsen & Toubro (L&T)",
            "Reliance Industries Ltd",
            "Reliance Jio Infocomm Ltd",
            "JSW Steel Ltd.",
            "Essel Mumbai WTR Pvt. Ltd.",
            "National Sports Club of India (NSCI)",
        ],
    },
];

const total = groups.reduce((sum, g) => sum + g.organizations.length, 0);

const Credentials = () => {
    return (
        <section className="bg-gray-50 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Intro */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="grid md:grid-cols-[auto,1fr] gap-6 md:gap-10 items-center rounded-3xl border border-gray-100 bg-white p-8 md:p-12 shadow-lg"
                >
                    <div className="flex md:flex-col items-center gap-4 md:gap-3 text-center">
                        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-navy text-3xl text-brand-yellow">
                            <FaAward aria-hidden="true" />
                        </span>
                        <div>
                            <p className="text-4xl font-bold font-display text-brand-navy">{total}</p>
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Organisations</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
                            Due to the successful completion of a wide range of projects over the past <span className="font-bold text-brand-navy">26 years</span>, our company has been recognized as one of the leaders in civil construction development.
                        </p>
                        <p className="mt-4 text-sm sm:text-base md:text-lg text-gray-600">
                            Our expertise has been acknowledged by numerous large-scale public and private organizations:
                        </p>
                    </div>
                </motion.div>

                {/* ISO certification */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="mt-6 flex flex-col sm:flex-row items-center gap-5 rounded-3xl bg-brand-navy p-6 sm:p-8 text-center sm:text-left shadow-lg"
                >
                    <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-brand-yellow text-3xl text-brand-navy">
                        <FaCertificate aria-hidden="true" />
                    </span>
                    <div className="flex-1">
                        <p className="text-xl sm:text-2xl font-bold font-display text-white">{ISO.standard} Certified</p>
                        <p className="mt-1 text-blue-100">
                            Our quality management system is certified to the {ISO.standard} standard.
                        </p>
                    </div>
                    <p className="shrink-0 rounded-full border border-brand-yellow/60 px-4 py-1.5 text-sm font-semibold text-brand-yellow">
                        Certificate No. {ISO.certificateNo}
                    </p>
                </motion.div>

                {/* Groups */}
                <div className="mt-10 grid lg:grid-cols-3 gap-6 items-start">
                    {groups.map(({ title, icon: Icon, organizations }, i) => (
                        <motion.article
                            key={title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.45, delay: i * 0.08 }}
                            className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md"
                        >
                            <header className="flex items-center gap-3 bg-brand-navy px-5 py-4 text-white">
                                <Icon className="text-xl text-brand-yellow" aria-hidden="true" />
                                <h3 className="flex-1 font-bold leading-snug">{title}</h3>
                                <span
                                    className="rounded-full bg-brand-yellow px-2.5 py-0.5 text-xs font-bold text-brand-navy"
                                    aria-label={`${organizations.length} organisations`}
                                >
                                    {organizations.length}
                                </span>
                            </header>
                            <ul className="divide-y divide-gray-100">
                                {organizations.map((org) => (
                                    <li key={org} className="flex items-start gap-3 px-5 py-3.5 text-sm sm:text-base text-gray-800">
                                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-yellow/40 text-[10px] text-brand-navy">
                                            <FaCheck aria-hidden="true" />
                                        </span>
                                        {org}
                                    </li>
                                ))}
                            </ul>
                        </motion.article>
                    ))}
                </div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-2xl bg-gradient-to-r from-brand-navy to-brand-navy-light p-8 shadow-xl"
                >
                    <p className="text-center sm:text-left text-lg md:text-xl font-semibold text-white">
                        Ready to join our list of satisfied clients?
                    </p>
                    <Link
                        to="/contact-us"
                        className="shrink-0 inline-block rounded-full bg-brand-yellow px-8 py-3 font-bold text-brand-navy shadow-lg transition-all duration-300 hover:bg-brand-gold hover:-translate-y-0.5"
                    >
                        Contact Us Today
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default Credentials;
